import {Trans} from '@lingui/macro';
import {chain, uniq} from 'lodash';
import React, {useCallback, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {VisualSizesEnum} from '../../../../../../../libs/shared/ui/src/styles/commonStyles';
import {useAppDispatch} from '../../../../../../helpers/reduxHelpers';
import {useFetchSafetyNets} from '../../../../../../helpers/safetyNetsHelpers';
import {ConversationBaseModel} from '../../../../../../models/api/conversationModel';
import {isConversationSafetyNetRequiredTagging} from '../../../../../../models/api/conversationSafetyNetModel';
import {addAndRemoveTagsFromConversationsAction} from '../../../../../../store/data/conversations/tagConversationsActions';
import {OkCancelDialog} from '../../../../../common/dialogs/okCancelDialog';
import {AsyncLoader} from '../../../../../common/loaders/asyncLoader';
import {Skeleton, SkeletonColorSchemesEnum} from '../../../../../common/skeleton';
import {SafetyNetAction, SafetyNetActionsEnum} from '../safetyNetsContext';
import {requiredTaggingLabels} from './requiredTaggingLabels';
import {RequiredTaggingTooltip} from './requiredTaggingTooltip';
import {RequiredTagList} from './requiredTagList';

/*
 * Props.
 */

interface RequiredTaggingDialogProps {
  onHideBlockingModal: Handler;
  safetyNetAction: SafetyNetAction;
}

/*
 * Types.
 */

interface TagUpdates {
  add: ReadonlyArray<number>;
  remove: ReadonlyArray<number>;
}

/*
 * Styles.
 */

const StyledTagListDiv = styled.div`
  margin: 20px 0;
`;

const StyledSpacer = styled.div<{$height: string}>`
  height: ${(p) => p.$height};
  margin: 0;
`;

const StyledSkeletonContainerDiv = styled.div`
  height: 65px;
  overflow: hidden;
`;

const StyledDialogTitleDiv = styled.div`
  display: flex;
  align-items: center;
`;

const StyledRequiredTaggingTooltip = styled(RequiredTaggingTooltip)`
  margin-left: 4px;
`;

/*
 * Component.
 */

export const RequiredTaggingDialog: React.FC<RequiredTaggingDialogProps> = (props) => {
  const {onHideBlockingModal, safetyNetAction} = props;
  const {conversations} = safetyNetAction;
  const [conversationIndex, setConversationIndex] = useState(0);

  const conversation = conversations[conversationIndex];

  const isSingleConversation = conversations.length === 1;
  const isLastConversationSelected = conversationIndex === conversations.length - 1;

  const safetyNetIds = useMemo(
    () =>
      (conversation.safetyNets ?? [])
        .filter(isConversationSafetyNetRequiredTagging)
        .map((conversationSafetyNet) => conversationSafetyNet.id),
    [conversation.safetyNets],
  );

  const {safetyNets, loadingStatus, onRetry} = useFetchSafetyNets(safetyNetIds);

  const {generateUpdatesPatch, onAddTag, onRemoveTag, selectedTagIds, resetState} =
    useSafetyNetState(conversation);

  const areAllSafetyNetsSatisfied = useMemo(
    () =>
      safetyNets.every((safetyNet) =>
        safetyNet.dependencyIds.some((tagId) => selectedTagIds.includes(tagId)),
      ),
    [safetyNets, selectedTagIds],
  );

  const dispatch = useAppDispatch();

  const onRequestClose = useCallback(() => {
    onHideBlockingModal();
  }, [onHideBlockingModal]);

  const onOkClick = useCallback(() => {
    dispatch(addAndRemoveTagsFromConversationsAction([conversation.id], generateUpdatesPatch()));

    if (isLastConversationSelected) {
      safetyNetAction.onActionValidated();
      onRequestClose();
      return;
    }

    resetState();
    setConversationIndex(conversationIndex + 1);
  }, [
    conversation.id,
    conversationIndex,
    dispatch,
    generateUpdatesPatch,
    isLastConversationSelected,
    onRequestClose,
    resetState,
    safetyNetAction,
  ]);

  return (
    <OkCancelDialog
      title={
        <StyledDialogTitleDiv>
          <Trans id="required-tagging-dialog-title">Tagging required</Trans>
          <StyledRequiredTaggingTooltip conversations={conversations} />
        </StyledDialogTitleDiv>
      }
      okButtonLabel={renderOkButton(safetyNetAction.type, isLastConversationSelected)}
      isOkDisabled={!areAllSafetyNetsSatisfied || loadingStatus !== AsyncStatusesEnum.LOADED}
      onRequestClose={onRequestClose}
      onOkClick={onOkClick}
    >
      {requiredTaggingLabels[safetyNetAction.type]?.renderDescription(
        !isSingleConversation ? conversation : undefined,
      )}
      <StyledTagListDiv>
        {loadingStatus !== AsyncStatusesEnum.LOADED
          ? renderLoadingOrError(loadingStatus, safetyNetIds, onRetry)
          : safetyNets.map((safetyNet) => (
              <RequiredTagList
                key={safetyNet.id}
                safetyNet={safetyNet}
                onAddTag={onAddTag}
                onRemoveTag={onRemoveTag}
                selectedTagIds={selectedTagIds}
              />
            ))}
      </StyledTagListDiv>
    </OkCancelDialog>
  );
};

/*
 * Helpers.
 */

function renderOkButton(actionType: SafetyNetActionsEnum, isLastConversationSelected: boolean) {
  if (isLastConversationSelected) {
    return requiredTaggingLabels[actionType]?.okButton;
  }

  return <Trans id="required-tagging-dialog-ok-continue">Tag & Continue</Trans>;
}

const defaultTagUpdates: TagUpdates = {add: [], remove: []};

function useSafetyNetState(conversation: ConversationBaseModel) {
  const conversationTagIds = conversation.tagIds;

  const [tagUpdates, setTagUpdates] = useState<TagUpdates>(defaultTagUpdates);

  const onAddTag = useCallback((tagId: number) => {
    setTagUpdates((localTagUpdates) => ({
      add: uniq([...localTagUpdates.add, tagId]),
      remove: localTagUpdates.remove.filter((tagUpdateId) => tagUpdateId !== tagId),
    }));
  }, []);

  const onRemoveTag = useCallback((tagId: number) => {
    setTagUpdates((localTagUpdates) => ({
      add: localTagUpdates.add.filter((tagUpdateId) => tagUpdateId !== tagId),
      remove: uniq([...localTagUpdates.remove, tagId]),
    }));
  }, []);

  const generateUpdatesPatch = useCallback(() => {
    // If the conversation have already the tag, do not re-tag it again.
    const addTags = tagUpdates.add.filter((addTag) => !conversationTagIds.includes(addTag));
    // Only untag if the conversation has the tag.
    const removeTags = tagUpdates.remove.filter((removeTag) => conversationTagIds.includes(removeTag));

    return {
      add: addTags,
      remove: removeTags,
    };
  }, [conversationTagIds, tagUpdates.add, tagUpdates.remove]);

  const currentSelectedTags = useMemo(
    () =>
      chain([...conversationTagIds, ...tagUpdates.add])
        .without(...tagUpdates.remove)
        .uniq()
        .value(),
    [conversationTagIds, tagUpdates.add, tagUpdates.remove],
  );

  const resetState = useCallback(() => {
    setTagUpdates(defaultTagUpdates);
  }, []);

  return {
    onAddTag,
    onRemoveTag,
    selectedTagIds: currentSelectedTags,
    generateUpdatesPatch,
    resetState,
  };
}

function renderLoadingOrError(
  loadingStatus: AsyncStatusesEnum,
  safetyNetIds: ReadonlyArray<number>,
  onRetry: Handler,
) {
  if (loadingStatus === AsyncStatusesEnum.LOADING) {
    return safetyNetIds.map((safetyNetId) => (
      <StyledSkeletonContainerDiv key={safetyNetId}>
        <Skeleton width="78px" height="15px" scheme={SkeletonColorSchemesEnum.GREY30} />
        <StyledSpacer $height="6px" />
        <Skeleton height="30px" scheme={SkeletonColorSchemesEnum.GREY10} />
      </StyledSkeletonContainerDiv>
    ));
  }

  if (loadingStatus === AsyncStatusesEnum.FAILED) {
    return (
      <AsyncLoader status={AsyncStatusesEnum.FAILED} onRetryClick={onRetry} size={VisualSizesEnum.MEDIUM} />
    );
  }

  return null;
}
